import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router";
import { toast } from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import { Settings, ShieldCheck, User } from "lucide-react";
import { cn, getInitials } from "@/lib/utils";
import { useAuthStore } from "@/store/authStore";
import { Badge, brandFillClass } from "@/components/ui";
import { PageFrame, SectionCard } from "@/components/common";
import {
  legalInfoQueryKey,
  profileService,
  type UserLegalInfo,
} from "@/services/profileService";
import { ProfileSettingsForm } from "@/components/profile";
import { ProfileStatusBanner } from "./components/ProfileStatusBanner";

export default function SettingsPage() {
  const { t } = useTranslation("settings");
  const { user, isAdmin } = useAuthStore();
  const queryClient = useQueryClient();
  const userId = user?.sub;
  const [searchParams, setSearchParams] = useSearchParams();
  const contactEmailVerifiedHandled = useRef(false);

  const {
    data: profileData,
    isLoading,
    isError,
    error: profileError,
  } = useQuery({
    queryKey: legalInfoQueryKey(userId ?? ""),
    queryFn: () => profileService.getProfile(userId!),
    enabled: !!userId && !isAdmin(),
  });

  useEffect(() => {
    if (isError) {
      toastApiError(profileError, "generic", t("errorLoadingProfile"));
    }
  }, [isError, profileError, t]);

  useEffect(() => {
    if (
      contactEmailVerifiedHandled.current ||
      !userId ||
      isAdmin() ||
      searchParams.get("contactEmailVerified") !== "1"
    ) {
      return;
    }
    contactEmailVerifiedHandled.current = true;
    toast.success(t("forms.contact.verifyEmailSuccess"));
    void queryClient.invalidateQueries({ queryKey: legalInfoQueryKey(userId) });
    const next = new URLSearchParams(searchParams);
    next.delete("contactEmailVerified");
    setSearchParams(next, { replace: true });
  }, [userId, isAdmin, searchParams, setSearchParams, queryClient, t]);

  const handleProfileUpdated = useCallback(
    (profile: UserLegalInfo) => {
      if (!userId) return;
      queryClient.setQueryData(legalInfoQueryKey(userId), profile);
    },
    [queryClient, userId],
  );

  if (isAdmin()) {
    return (
      <PageFrame
        title={t("adminPageTitle")}
        description={t("adminPageDescription")}
        icon={Settings}
      >
        <SectionCard
          title={t("adminCardTitle")}
          icon={User}
        >
          <div className="flex items-center gap-4">
            <div className={cn("shadow-elevated flex h-14 w-14 shrink-0 items-center justify-center rounded-xl text-lg font-semibold", brandFillClass("primary"))}>
              {getInitials(user?.email ?? "")}
            </div>
            <div className="min-w-0">
              <p className="text-surface-900 truncate text-base font-semibold tracking-tight">
                {user?.email}
              </p>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <Badge
                  variant="primary"
                  size="sm"
                >
                  <ShieldCheck
                    className="mr-1 h-3 w-3"
                    aria-hidden
                  />
                  {t("adminBadge")}
                </Badge>
              </div>
            </div>
          </div>
        </SectionCard>
      </PageFrame>
    );
  }

  return (
    <PageFrame
      title={t("pageTitle")}
      description={t("profileDescription")}
      icon={Settings}
      isLoading={isLoading || !profileData || !userId}
      loadingMessage={t("loadingProfile")}
    >
      {profileData && userId && user ? (
        <>
          {profileData.profile_completed ? null : (
            <ProfileStatusBanner />
          )}

          <ProfileSettingsForm
            key={`${userId}-${profileData.id}-${profileData.profile_type}`}
            profile={profileData}
            userEmail={user.email}
            userId={userId}
            onProfileUpdated={handleProfileUpdated}
            showSectionNav
          />
        </>
      ) : null}
    </PageFrame>
  );
}
